import { EventEmitter, Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { List } from 'immutable';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { FlashMessagesService } from '../../shared/flash-messages/service/flash.messages.service';
import { Reservation } from '../models/reservation';
import { ReservationService } from './reservation-http-service';
import { RoomTypeRateSummary, RoomTypeRateSummarySort } from '../models/room-type-rate-summary';
import { StaySummary } from '../models/stay-summary';
import { ReservationStatusCode } from 'app/event/models/reservation-status-code.enum';
import { RoomStaySummary } from '../models/room-stay-summary';
import { ReservationCompareService } from './reservation-compare-http-service';
import { ReservationCompare } from '../models/reservation-compare';
import { ReservationUtils } from '../utils/reservation-utils';
import { MatchTypeEnum } from '../models/match-type.enum';
import { Rsvp } from '../models/rsvp';

@Injectable()
export class ReservationCompareStore {


  public static readonly STATUS_CODES = [
    ReservationStatusCode.RSVP,
    ReservationStatusCode.RESERVED,
    ReservationStatusCode.NO_SHOW,
    ReservationStatusCode.CANCELLED,
    ReservationStatusCode.CHECKED_OUT,
    ReservationStatusCode.CHECKED_IN ];

  private currentReservationList: BehaviorSubject<List<ReservationCompare>> =
    new BehaviorSubject(List([]));

  private currentActiveStatusCodes: BehaviorSubject<Array<ReservationStatusCode>> =
    new BehaviorSubject(ReservationCompareStore.STATUS_CODES);

  private currentReservationSummaryState: BehaviorSubject<boolean> =
    new BehaviorSubject(true);

  public selectedRsvpId: number = null;
  public selectedReservationId: number = null;


  constructor(private flashMessagesService: FlashMessagesService,
              private reservationCompareService: ReservationCompareService) {
  }

  setActiveStatusCodes(statusCodes: Array<ReservationStatusCode>): void {
    this.currentActiveStatusCodes.next(statusCodes);
  }

  getActiveStatusCodes(): Observable<Array<ReservationStatusCode>> {
    return this.currentActiveStatusCodes.asObservable().share();
  }

  getInstantActiveStatusCodes(): Array<ReservationStatusCode> {
    return this.currentActiveStatusCodes.getValue();
  }

  getReservationList(): Observable<List<ReservationCompare>> {
    return this.currentReservationList.asObservable();
  }

  getShowReservationSummary(): Observable<boolean> {
    return this.currentReservationSummaryState.asObservable();
  }

  setShowReservationSummary(showSummary: boolean): void {
    return this.currentReservationSummaryState.next(showSummary);
  }

  getReservations(eventId: number): Observable<List<ReservationCompare>> {
    return this.reservationCompareService.get(eventId)
      .catch((error: any) => {
        this.flashMessagesService.error(
          'MESSAGES.ERROR.RESERVATION_COMPARE',
          `Error retrieving room details: ${error}`
        );
        return Observable.throw(error);
      })
      .map((reservationList: List<ReservationCompare>) => {
        reservationList = reservationList.sort(ReservationUtils.reservationCompareComparator).toList();
        this.currentReservationList.next(reservationList);
        return reservationList;
      });
  }

  getFilteredReservations(eventId: number): Observable<List<ReservationCompare>> {
    return this.getActiveStatusCodes().flatMap((statusCodes: ReservationStatusCode[]) => {
      return this.getReservations(eventId).map(reservationList => {
        return this.getReservationListFilteredByStatus(reservationList, statusCodes);
      });
    });
  }

  getCurrentFilteredReservations(): Observable<List<ReservationCompare>> {
    return this.getActiveStatusCodes().flatMap((statusCodes: ReservationStatusCode[]) => {
      return this.getReservationList().map(reservationList => {
        return this.getReservationListFilteredByStatus(reservationList, statusCodes);
      });
    });
  }

  getReservationListFilteredByStatus(reservationList: List<ReservationCompare>,
                                     statusCodes: ReservationStatusCode[]): List<ReservationCompare> {
    return reservationList.filter((reservation: ReservationCompare) => {
      return statusCodes.find(statusCode => statusCode.toString() === reservation.status) != null;
    }).toList();
  }

  resetStatusCodes(): void {
    this.setActiveStatusCodes(ReservationCompareStore.STATUS_CODES);
  }

  resetReservationList(): void {
    this.setReservationList(List([]));
  }

  setReservationList(list: List<ReservationCompare>): void {
    this.currentReservationList.next(list);
  }

  isFilteredMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getCurrentFilteredReservations().map((comparisonList: List<ReservationCompare>) => {
      return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    }));
  }

  isMatchListEmpty(matchType: MatchTypeEnum): Observable<boolean> {
    return ReservationUtils.isListEmpty(this.getReservationList().map((comparisonList: List<ReservationCompare>) => {
      return comparisonList.filter((comparison: ReservationCompare) => comparison.matchType === matchType.toString()).toList();
    }));
  }

  areRsvpsPresent(): Observable<boolean> {
    return ReservationUtils.getNumberOfRsvps(this.getReservationList()).map(size => {
      return size > 0;
    });
  }

}



// WEBPACK FOOTER //
// /var/lib/jenkins/jobs/UAT-GroupPay/workspace/pioneer-web/src/app/event/service/reservation-compare-store.ts